import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Heart, Flame } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useGameStore } from '../store/gameStore';
import { formatTime } from '../utils/gameLogic';
import { soundManager } from '../services/webAudioSound';
import Button from '../components/common/Button';
import { Card } from '@/components/ui/card';

const RANDOM_LEVEL_PARAM = 'random';

const GAME_TYPE_LABELS: Record<string, string> = {
  multiplication: 'Multiply',
  division:       'Divide',
  addition:       'Add',
  subtraction:    'Subtract',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  easy:   '🌱 Easy',
  medium: '⚡ Medium',
  hard:   '🔥 Hard',
};

export default function GamePlay() {
  const { level } = useParams<{ level: string }>();
  const navigate = useNavigate();
  const {
    currentQuestion,
    score,
    lives,
    questionsAnswered,
    currentStreak,
    isPaused,
    gameType,
    difficulty,
    startLevel,
    startRandomMode,
    answerQuestion,
    nextQuestion,
    pauseGame,
    resumeGame,
    quitLevel
  } = useGameStore();

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'correct' | 'wrong' | null>(null);
  const [timer, setTimer] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);

  const isRandom = level === RANDOM_LEVEL_PARAM;

  useEffect(() => {
    if (level) {
      if (isRandom) {
        startRandomMode();
      } else {
        startLevel(parseInt(level));
      }
    }
  }, [level, startLevel, startRandomMode]);

  useEffect(() => {
    if (!isPaused && !showComplete && !showGameOver) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPaused, showComplete, showGameOver]);

  useEffect(() => {
    if (questionsAnswered >= 10 && lives > 0) {
      setShowComplete(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      soundManager.play('complete');
    } else if (lives === 0) {
      setShowGameOver(true);
      soundManager.play('gameOver');
    }
  }, [questionsAnswered, lives]);

  const handleAnswer = async (answer: number) => {
    if (showFeedback || !currentQuestion) return;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    setFeedbackType(isCorrect ? 'correct' : 'wrong');
    soundManager.play(isCorrect ? 'correct' : 'wrong');
    
    if (isCorrect) {
      confetti({
        particleCount: 30,
        spread: 50,
        origin: { y: 0.5 },
        colors: ['#10b981', '#34d399', '#6ee7b7']
      });
    }
    
    await answerQuestion(answer);
    
    setTimeout(() => {
      setSelectedAnswer(null);
      setShowFeedback(false);
      setFeedbackType(null);
      if (questionsAnswered < 9 && lives > 0) {
        nextQuestion();
      }
    }, 1500);
  };

  const handleRestart = () => {
    setTimer(0);
    setShowComplete(false);
    setShowGameOver(false);
    if (isRandom) {
      startRandomMode();
    } else {
      startLevel(parseInt(level!));
    }
  };

  const handleNextLevel = () => {
    const nextLevel = parseInt(level!) + 1;
    setShowComplete(false);
    setShowGameOver(false);
    setTimer(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setFeedbackType(null);
    
    if (nextLevel <= 10) {
      navigate(`/play/${nextLevel}`);
    } else {
      navigate('/levels');
    }
  };

  if (!currentQuestion) return null;

  const gameTypeLabel = GAME_TYPE_LABELS[gameType] || 'Multiply';
  const difficultyLabel = DIFFICULTY_LABELS[difficulty] || '⚡ Medium';
  const levelTitle = isRandom ? `Random Mix · ${gameTypeLabel}` : `${gameTypeLabel} · Level ${level}`;

  return (
    <motion.div 
      className="min-h-screen p-4 relative flex items-center justify-center transition-colors duration-300"
      animate={{
        backgroundColor: feedbackType === 'correct' 
          ? 'rgba(16, 185, 129, 0.1)' 
          : feedbackType === 'wrong' 
          ? 'rgba(239, 68, 68, 0.1)' 
          : 'rgba(0, 0, 0, 0)'
      }}
    >
      <motion.div 
        className="w-full max-w-4xl"
        animate={{
          x: feedbackType === 'wrong' ? [0, -10, 10, -10, 10, 0] : 0
        }}
        transition={{ duration: 0.5 }}
      >
        <Card className="bg-gradient-to-br from-slate-900/90 to-purple-900/90 backdrop-blur border-white/20 p-8">
          <div className="flex justify-between items-center mb-6">
            <Button variant="ghost" size="sm" onClick={pauseGame}>
              <Pause className="w-5 h-5" />
            </Button>
            
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">{levelTitle}</h2>
              <p className="text-purple-300 text-sm">Score: {score} &nbsp;·&nbsp; {difficultyLabel}</p>
            </div>
            
            <div className="flex gap-1">
              {[1, 2, 3].map((heart) => (
                <Heart
                  key={heart}
                  className={`w-6 h-6 ${heart <= lives ? 'text-red-500 fill-red-500' : 'text-gray-600'}`}
                />
              ))}
            </div>
          </div>

          <div className="mb-6">
            <div className="bg-white/10 rounded-full h-4 overflow-hidden">
              <motion.div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-full"
                initial={{ width: 0 }}
                animate={{ width: `${(questionsAnswered / 10) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-center text-white mt-2">{questionsAnswered}/10</p>
          </div>

          <div className="text-center mb-8">
            <motion.div
              key={currentQuestion.id}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-6xl font-bold text-white mb-4"
            >
              {currentQuestion.num1} {currentQuestion.operator} {currentQuestion.num2} = ?
            </motion.div>
            
            {currentStreak >= 3 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center justify-center gap-2 text-orange-400"
              >
                <Flame className="w-6 h-6" />
                <span className="text-xl font-bold">Streak: {currentStreak}</span>
              </motion.div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {currentQuestion.options.map((option, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <motion.div
                  animate={{
                    scale: showFeedback && selectedAnswer === option && feedbackType === 'correct' 
                      ? [1, 1.1, 1] 
                      : showFeedback && selectedAnswer === option && feedbackType === 'wrong'
                      ? [1, 0.95, 1]
                      : 1
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Button
                    size="lg"
                    variant={
                      showFeedback && selectedAnswer === option
                        ? option === currentQuestion.correctAnswer
                          ? 'primary'
                          : 'danger'
                        : 'secondary'
                    }
                    onClick={() => handleAnswer(option)}
                    disabled={showFeedback}
                    className={`w-full text-2xl py-8 transition-all ${
                      showFeedback && option === currentQuestion.correctAnswer
                        ? 'ring-4 ring-green-500 bg-green-600 hover:bg-green-700'
                        : showFeedback && selectedAnswer === option && feedbackType === 'wrong'
                        ? 'ring-4 ring-red-500 animate-pulse'
                        : ''
                    }`}
                  >
                    {option}
                  </Button>
                </motion.div>
              </motion.div>
            ))}
          </div>

          <div className="text-center text-xl text-gray-300">
            Time: {formatTime(timer)}
          </div>
        </Card>
      </motion.div>

      <AnimatePresence>
        {isPaused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50"
          >
            <Card className="p-8 bg-slate-900/90 border-white/20 max-w-md w-full">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Game Paused</h2>
              <div className="space-y-4">
                <Button size="lg" gradient onClick={resumeGame} className="w-full">
                  Resume
                </Button>
                <Button size="lg" variant="secondary" onClick={handleRestart} className="w-full">
                  Restart Level
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => {
                    quitLevel();
                    setShowComplete(false);
                    setShowGameOver(false);
                    setTimer(0);
                    navigate('/levels');
                  }}
                  className="w-full"
                >
                  Quit to Menu
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {showComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50"
          >
            <Card className="p-8 bg-gradient-to-br from-green-900/90 to-emerald-900/90 border-green-500/50 max-w-md w-full">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring" }}
                className="text-6xl text-center mb-4"
              >
                🏆
              </motion.div>
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Mission Complete!</h2>
              
              <div className="space-y-2 mb-6 text-white">
                <div className="flex justify-between">
                  <span>Score:</span>
                  <span className="font-bold">{score}</span>
                </div>
                <div className="flex justify-between">
                  <span>Accuracy:</span>
                  <span className="font-bold">{Math.round((questionsAnswered / 10) * 100)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Time:</span>
                  <span className="font-bold">{formatTime(timer)}</span>
                </div>
              </div>

              <div className="space-y-4">
                {!isRandom && parseInt(level!) < 10 && (
                  <Button size="lg" gradient onClick={handleNextLevel} className="w-full">
                    Next Mission
                  </Button>
                )}
                <Button size="lg" variant="secondary" onClick={handleRestart} className="w-full">
                  {isRandom ? 'Play Again' : 'Try Again'}
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => {
                    setShowComplete(false);
                    setShowGameOver(false);
                    navigate('/levels');
                  }}
                  className="w-full"
                >
                  Select Level
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {showGameOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50"
          >
            <Card className="p-8 bg-gradient-to-br from-red-900/90 to-orange-900/90 border-red-500/50 max-w-md w-full">
              <div className="text-6xl text-center mb-4">😢</div>
              <h2 className="text-3xl font-bold text-white mb-4 text-center">Mission Failed</h2>
              <p className="text-gray-300 text-center mb-6">Don't give up, Space Cadet!</p>
              
              <div className="space-y-4">
                <Button size="lg" gradient onClick={handleRestart} className="w-full">
                  {isRandom ? 'Play Again' : 'Try Again'}
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => {
                    setShowComplete(false);
                    setShowGameOver(false);
                    navigate('/levels');
                  }}
                  className="w-full"
                >
                  Select Level
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}